import { ORIENTATIONS } from "../../constants/intxConstants";
import { ADJUSTMENT_FACTORS } from "../../constants/intxConstants";

/**
 * @typedef {Object} Volume
 */

/** Class to hold essential volume input information for turning movements of a single directional approach. */
export class Volume {
  /**
   * Creates the Volume class object, turning movement counts in veh/hr. Truck percent 0-100 (not 0-1)
   * @param {number} leftTurn - Left turn movement volume in veh/hr
   * @param {number} through - Through movement volume in veh/hr
   * @param {number} rightTurn - Right turn movement volume in veh/hr
   * @param {number} truckPct - Truck percentage associated with the direction approach (0-100)
   */
  constructor({ left, through, right, truckPct = 0 }) {
    this._leftTurn = left;
    this._through = through;
    this._righTurn = right;
    this._truckPct = truckPct;
  }
  /** @return {number} - Left turn movement volume in veh/hr */
  get LT() {
    return this._leftTurn;
  }
  /** @return {number} - Through movement volume in veh/hr */
  get T() {
    return this._through;
  }
  /** @return {number} - Right turn movement volume in veh/hr */
  get RT() {
    return this._righTurn;
  }
  /** @return {number} - Truck percentage associated with the direction approach (0-100) */
  get truckPct() {
    return this._truckPct;
  }
  /** @return {number} Decimal (0 - 1.0) value for the truck percent */
  get truckDec() {
    return this._truckPct / 100.0;
  }
}

/** Abstract Super class to form the basis for all intersection implementations */
export class Intersection {
  /**
   * Abstract Class - defines the constructor of the Intersection super class
   * @param {string} name - Name of the intersection
   * @param {Object} volumes - Object mapping direction strings (eastbound, westbound, northbound, southbound) to  {@link Volume} objects.
   * @param {Object} globalParams - Object containing the required global parameters for an intersection.
   */
  constructor(name, volumes, globalParams) {
    this._name = name;

    this._maxVC = -1.0;
    this._maxCLV = -1.0;
    this._globalParams = globalParams;
    this.setMasterVolumes(volumes);

    this._resultsByZone = {};
  }

  setMasterVolumes(volumes) {
    this._eastbound = volumes.eastbound;
    this._westbound = volumes.westbound;
    this._northbound = volumes.northbound;
    this._southbound = volumes.southbound;

    let TAF = this.TAF;
    // Compute the adjusted master volumes
    this._masterVolumes = {
      EBL: Math.round(
        volumes.eastbound.LT * (1 - volumes.eastbound.truckPct / 100) +
          ((volumes.eastbound.LT * volumes.eastbound.truckPct) / 100) * TAF
      ),
      EBT: Math.round(
        volumes.eastbound.T * (1 - volumes.eastbound.truckPct / 100) +
          ((volumes.eastbound.T * volumes.eastbound.truckPct) / 100) * TAF
      ),
      EBR: Math.round(
        volumes.eastbound.RT * (1 - volumes.eastbound.truckPct / 100) +
          ((volumes.eastbound.RT * volumes.eastbound.truckPct) / 100) * TAF
      ),
      WBL: Math.round(
        volumes.westbound.LT * (1 - volumes.westbound.truckPct / 100) +
          ((volumes.westbound.LT * volumes.westbound.truckPct) / 100) * TAF
      ),
      WBT: Math.round(
        volumes.westbound.T * (1 - volumes.westbound.truckPct / 100) +
          ((volumes.westbound.T * volumes.westbound.truckPct) / 100) * TAF
      ),
      WBR: Math.round(
        volumes.westbound.RT * (1 - volumes.westbound.truckPct / 100) +
          ((volumes.westbound.RT * volumes.westbound.truckPct) / 100) * TAF
      ),
      NBL: Math.round(
        volumes.northbound.LT * (1 - volumes.northbound.truckPct / 100) +
          ((volumes.northbound.LT * volumes.northbound.truckPct) / 100) * TAF
      ),
      NBT: Math.round(
        volumes.northbound.T * (1 - volumes.northbound.truckPct / 100) +
          ((volumes.northbound.T * volumes.northbound.truckPct) / 100) * TAF
      ),
      NBR: Math.round(
        volumes.northbound.RT * (1 - volumes.northbound.truckPct / 100) +
          ((volumes.northbound.RT * volumes.northbound.truckPct) / 100) * TAF
      ),
      SBL: Math.round(
        volumes.southbound.LT * (1 - volumes.southbound.truckPct / 100) +
          ((volumes.southbound.LT * volumes.southbound.truckPct) / 100) * TAF
      ),
      SBT: Math.round(
        volumes.southbound.T * (1 - volumes.southbound.truckPct / 100) +
          ((volumes.southbound.T * volumes.southbound.truckPct) / 100) * TAF
      ),
      SBR: Math.round(
        volumes.southbound.RT * (1 - volumes.southbound.truckPct / 100) +
          ((volumes.southbound.RT * volumes.southbound.truckPct) / 100) * TAF
      ),
    };
  }

  /** @return {string} - Name of the intersection */
  get name() {
    return this._name;
  }

  set name(newName) {
    this._name = newName;
  }

  /** @return {Volume} - Volume object for the eastbound directional approach. */
  get eastbound() {
    return this._eastbound;
  }
  /** @return {Volume} - Volume object for the westbound directional approach. */
  get westbound() {
    return this._westbound;
  }
  /** @return {Volume} - Volume object for the northbound directional approach. */
  get northbound() {
    return this._northbound;
  }
  /** @return {Volume} - Volume object for the southbound directional approach. */
  get southbound() {
    return this._southbound;
  }

  /** @return {number} - Maximum V/C Ratio value (across all zones) for the intersection. */
  get maxVC() {
    return this._maxVC;
  }

  /** @return {number} - Maximum critical lane volume (CLV) (across all zones) for the intersection. */
  get maxCLV() {
    return this._maxCLV;
  }

  /** @return {Object} - Global parameters object for the intersection. */
  get globalParams() {
    return this._globalParams;
  }

  /** @return {Object} - Object mapping direction strings (eastbound, westbound, northbound, southbound) to  {@link Volume} objects. */
  get masterVolumes() {
    return this._masterVolumes;
  }

  /** @return {number} - Eastbound left turn movement volume. */
  get EBL_MASTER() {
    return this._masterVolumes.EBL;
  }
  /** @return {number} - Eastbound through movement volume. */
  get EBT_MASTER() {
    return this._masterVolumes.EBT;
  }
  /** @return {number} - Eastbound right turn movement volume. */
  get EBR_MASTER() {
    return this._masterVolumes.EBR;
  }
  /** @return {number} - Westbound left turn movement volume. */
  get WBL_MASTER() {
    return this._masterVolumes.WBL;
  }
  /** @return {number} - Westbound through movement volume. */
  get WBT_MASTER() {
    return this._masterVolumes.WBT;
  }
  /** @return {number} - Westbound right turn movement volume. */
  get WBR_MASTER() {
    return this._masterVolumes.WBR;
  }
  /** @return {number} - Northbound left turn movement volume. */
  get NBL_MASTER() {
    return this._masterVolumes.NBL;
  }
  /** @return {number} - Northbound through movement volume. */
  get NBT_MASTER() {
    return this._masterVolumes.NBT;
  }
  /** @return {number} - Northbound right turn movement volume. */
  get NBR_MASTER() {
    return this._masterVolumes.NBR;
  }
  /** @return {number} - Southbound left turn movement volume. */
  get SBL_MASTER() {
    return this._masterVolumes.SBL;
  }
  /** @return {number} - Southbound through movement volume. */
  get SBT_MASTER() {
    return this._masterVolumes.SBT;
  }
  /** @return {number} - Southbound right turn movement volume. */
  get SBR_MASTER() {
    return this._masterVolumes.SBR;
  }

  /** @return {number} - U-Turn adjustment factor (0 - 1); Conversion of U-turning vehicles to equivalent through vehicles. */
  get UTAF() {
    return this._globalParams?.utaf || ADJUSTMENT_FACTORS.UTAF;
  }

  /** @return {number} - Left-turn adjustment factor (0 - 1); Conversion of left-turning vehicles to equivalent through vehicles. */
  get LTAF() {
    return this._globalParams?.ltaf || ADJUSTMENT_FACTORS.LTAF;
  }

  /** @return {number} - Right-turn adjustment factor (0 - 1); Conversion of right-turning vehicles to equivalent through vehicles. */
  get RTAF() {
    return this._globalParams?.rtaf || ADJUSTMENT_FACTORS.RTAF;
  }

  /** @return {number} - Truck adjustment factor (0 - 1); Conversion of trucks to equivalent passenger cars. */
  get TAF() {
    return this._globalParams?.truckPCE || ADJUSTMENT_FACTORS.TRUCK_PCE;
  }

  // Unique in CapX: has 3 Critical Lane Volume Sum Limits for 2, 3, and 4 phase signals
  /** @return {number} - 4 Phase Critical Lane Volume Sum Limit; Saturation value for critical lane volume sum at an intersection.. */
  get CLV_Limit_4() {
    return (
      this._globalParams?.fourPhaseCLVSumLimit ||
      ADJUSTMENT_FACTORS.FOUR_PHASE_SIGNAL_CLV_SUM_LIMIT
    );
  }

  /** @return {number} - 3 Phase Critical Lane Volume Sum Limit; Saturation value for critical lane volume sum at an intersection. */
  get CLV_Limit_3() {
    return (
      this._globalParams?.threePhaseCLVSumLimit ||
      ADJUSTMENT_FACTORS.THREE_PHASE_SIGNAL_CLV_SUM_LIMIT
    );
  }

  /** @return {number} - 2 Phase Critical Lane Volume Sum Limit; Saturation value for critical lane volume sum at an intersection. */
  get CLV_Limit_2() {
    return (
      this._globalParams?.twoPhaseCLVSumLimit ||
      ADJUSTMENT_FACTORS.TWO_PHASE_SIGNAL_CLV_SUM_LIMIT
    );
  }

  /** @return {Object} - Object documenting the V/C ratio (VC) and critical lane volume (CLV) values for each zone (e.g. Z1, Z2, Z3, etc.) */
  get resultsByZone() {
    return this._resultsByZone;
  }

  /**
   * Get the type of the intersection as defined in the IntxBuilder static class.
   * @abstract
   * @return {string} Intersection type.
   * */
  get type() {
    throw new Error("must be implemented by subclass!");
  }

  /**
   * Function implementing the critical movement analysis for the intersection.
   * @abstract
   */
  _runCriticalMovementAnalysis() {
    throw new Error("must be implemented by subclass!");
  }

  /**
   * Assigns the results of the critical movement analyis to the maxVC and maxCLV properties.
   * @private
   */
  _assignMaxResults() {
    let tempMaxVC = 0;
    let tempMaxCLV = 0;
    for (const zoneNumId in this._resultsByZone) {
      if (this._resultsByZone.hasOwnProperty(zoneNumId)) {
        tempMaxVC = Math.max(tempMaxVC, this._resultsByZone[zoneNumId].VC || 0);
        tempMaxCLV = Math.max(
          tempMaxCLV,
          this._resultsByZone[zoneNumId].CLV || 0
        );
      }
    }
    this._maxVC = tempMaxVC;
    this._maxCLV = tempMaxCLV;
  }

  /**
   * Function computing the V/C ratios and critical lane volumes for the intersection.
   */
  computeVCAnalysis() {
    this._runCriticalMovementAnalysis();
    this._assignMaxResults();

    return {
      maxCLV: this._maxCLV,
      maxVC: this._maxVC,
      zoneResults: this._resultsByZone,
    };
  }

  /**
   * Computes the volume-capacity (VC) analysis based on changes to the volumes and lane configuration inputs.
   * If `volumes` is provided, updates the master volumes.
   * If `laneConfigInputs` is provided, updates the lane configuration inputs.
   * Computes the VC analysis after updating the inputs.
   * @param {Object} volumes - The volumes to use for the analysis.
   * @param {Object} laneConfigInputs - The lane configuration inputs to use for the analysis.
   */
  computeVCAnalysisOnChanges({
    volumes = undefined,
    laneConfigInputs = undefined,
  }) {
    if (volumes) {
      const volumesObject = {};
      for (const [key, value] of Object.entries(volumes)) {
        if (
          value.left !== undefined &&
          value.through !== undefined &&
          value.right !== undefined &&
          value.truckPct !== undefined
        ) {
          volumesObject[key] = new Volume(value);
        }
      }

      if (Object.keys(volumesObject).length === 0) {
        throw new Error("Invalid intersection volumes: " + volumes);
      }

      this.setMasterVolumes(volumesObject);
    }
    if (laneConfigInputs) {
      this.setLaneConfigInputs(laneConfigInputs);
    }

    this._runCriticalMovementAnalysis();
    this._assignMaxResults();

    return {
      maxCLV: this._maxCLV,
      maxVC: this._maxVC,
      zoneResults: this._resultsByZone,
    };
  }

  /**
   * Function that computes and returns the accommodation compared to Conventional for the intersection.
   * @abstract
   * @return {string} accommodation "+" "-" "0" for the intersection, default 0
   */
  getAccommodation() {
    return "0";
  }

  /**
   * Function to get the DEFAULT inputs available for a specific zone.  This function is designed to facilitate the
   * integration of the engine into a user interface.
   *
   * @abstract
   * @return {Object} Object representation of default inputs
   */
  static getZoneDefaultInputs() {
    throw new Error("Default inputs method be implemented by subclass!");
  }

  static getLaneConfigLabelsMapping() {
    return undefined;
  }

  /**
   * Function to set the lane configuration inputs of the alternative.
   * @abstract
   * @param laneConfigInputs
   */
  setLaneConfigInputs(laneConfigInputs) {
    throw new Error("Set Lane Config Inputs must be implemented by subclass!");
  }

  //-----------------------------------------------------------------------------------------------

  /**
   *  For an at-grade signalized or unsignalize intersection, generate an object with the volumes specified in
   *  Major1, Major2, Minor1, and Minor2 keys.  The volumes are assigned based on the major street direction passed to
   *  the function.
   *  The assignments are as given below:
   *
   *  For majorStreetDirection === EAST_WEST
   *  Major1 => EB
   *  Major2 => WB
   *  Minor1 => NB
   *  Minor2 => SB
   *
   *  For majorStreetDirection === NORTH_SOUTH
   *  Major1 => SB
   *  Major2 => NB
   *  Minor1 => EB
   *  Minor2 => WB
   *
   * @param majorStreetDirection
   * @return {Object} - Object mapping of the major and minor street approaches to volume objects.
   */
  generateDirectionalVolumes(majorStreetDirection) {
    let volumes = {};
    if (majorStreetDirection === ORIENTATIONS.NORTH_SOUTH) {
      volumes["Major1"] = new Volume({
        left: this.SBL_MASTER,
        through: this.SBT_MASTER,
        right: this.SBR_MASTER,
      });
      volumes["Major2"] = new Volume({
        left: this.NBL_MASTER,
        through: this.NBT_MASTER,
        right: this.NBR_MASTER,
      });
      volumes["Minor1"] = new Volume({
        left: this.EBL_MASTER,
        through: this.EBT_MASTER,
        right: this.EBR_MASTER,
      });
      volumes["Minor2"] = new Volume({
        left: this.WBL_MASTER,
        through: this.WBT_MASTER,
        right: this.WBR_MASTER,
      });
    } else {
      volumes["Major1"] = new Volume({
        left: this.EBL_MASTER,
        through: this.EBT_MASTER,
        right: this.EBR_MASTER,
      });
      volumes["Major2"] = new Volume({
        left: this.WBL_MASTER,
        through: this.WBT_MASTER,
        right: this.WBR_MASTER,
      });
      volumes["Minor1"] = new Volume({
        left: this.NBL_MASTER,
        through: this.NBT_MASTER,
        right: this.NBR_MASTER,
      });
      volumes["Minor2"] = new Volume({
        left: this.SBL_MASTER,
        through: this.SBT_MASTER,
        right: this.SBR_MASTER,
      });
    }
    return volumes;
  }

  /**
   *  For a grade separated interchange, generate an object with the volumes specified in Fwy1, Fwy2, Cross1, and Cross2 keys.
   *  The volumes are assigned based on the freeway direction passed to the function.
   *  The assignments are as given below:
   *
   *  For freewayDirection === EAST_WEST
   *  Fwy1 => EB
   *  Fwy2 => WB
   *  Cross1 => NB
   *  Cross2 => SB
   *
   *  For freewayDirection === NORTH_SOUTH
   *  Fwy1 => SB
   *  Fwy2 => NB
   *  Cross1 => EB
   *  Cross2 => WB
   *
   * @param freewayDirection
   * @return {Object} - Object mapping of the major and minor street approaches to volume objects.
   */
  generateInterchangeVolumes(freewayDirection) {
    let volumes = {};
    if (freewayDirection === ORIENTATIONS.EAST_WEST) {
      volumes["Fwy1"] = new Volume({
        left: this.EBL_MASTER,
        through: this.EBT_MASTER,
        right: this.EBR_MASTER,
      });
      volumes["Fwy2"] = new Volume({
        left: this.WBL_MASTER,
        through: this.WBT_MASTER,
        right: this.WBR_MASTER,
      });
      volumes["Cross1"] = new Volume({
        left: this.NBL_MASTER,
        through: this.NBT_MASTER,
        right: this.NBR_MASTER,
      });
      volumes["Cross2"] = new Volume({
        left: this.SBL_MASTER,
        through: this.SBT_MASTER,
        right: this.SBR_MASTER,
      });
    } else {
      volumes["Fwy1"] = new Volume({
        left: this.SBL_MASTER,
        through: this.SBT_MASTER,
        right: this.SBR_MASTER,
      });
      volumes["Fwy2"] = new Volume({
        left: this.NBL_MASTER,
        through: this.NBT_MASTER,
        right: this.NBR_MASTER,
      });
      volumes["Cross1"] = new Volume({
        left: this.EBL_MASTER,
        through: this.EBT_MASTER,
        right: this.EBR_MASTER,
      });
      volumes["Cross2"] = new Volume({
        left: this.WBL_MASTER,
        through: this.WBT_MASTER,
        right: this.WBR_MASTER,
      });
    }
    return volumes;
  }

  /**
   * Compute the "Left Turn Factor" based on the amount of opposing volume.
   * @param {number} opposingVols - Opposing volume to the left turn
   * @return {number} "Left turn factor"
   */
  leftTurnFactor(opposingVols) {
    if (opposingVols < 200) {
      // in excel sheet, it returns interger so 1.1 is returned as 1(refer to Custom Formulas in VBA modules in excel)
      return 1;
    } else if (opposingVols < 599) {
      return 2;
    } else if (opposingVols < 799) {
      return 3;
    } else if (opposingVols < 999) {
      return 4;
    } else {
      return 5;
    }
  }

  static get ControlType() {
    return { SIGNALIZED: "SIGNALIZED", UNSIGNALIZED: "UNSIGNALIZED" };
  }

  /**
   * function to get the labels mapping for the lane configuration inputs for orientation insensitive intersections
   * @returns {Object[]} - Array of objects with label and value properties for the lane configuration inputs
   */
  static getLabelsMappingWithoutMajorDir() {
    return [
      { label: "Eastbound", value: "EB" },
      { label: "Westbound", value: "WB" },
      { label: "Northbound", value: "NB" },
      { label: "Southbound", value: "SB" },
    ];
  }

  /**
   * function to get the labels mapping for the lane configuration inputs for orientation sensitive intersections
   * @param {bool} isEW  - true if the major street is east-west, false if the major street is north-south
   * @returns {Object[]} - Array of objects with label and value properties for the lane configuration inputs
   */
  static getIntersectionLabelsMappingWithMajorDir(isEW) {
    return [
      { label: "Eastbound", value: isEW ? "Major1" : "Minor1" },
      { label: "Westbound", value: isEW ? "Major2" : "Minor2" },
      { label: "Northbound", value: isEW ? "Minor1" : "Major2" },
      { label: "Southbound", value: isEW ? "Minor2" : "Major1" },
    ];
  }

  /**
   * function to get the labels mapping for the lane configuration inputs for orientation sensitive interchanges
   * @param {bool} isEW  - true if the freeway direction is east-west, false if the freeway direction is north-south
   * @returns {Object[]} - Array of objects with label and value properties for the lane configuration inputs
   */
  static getInterchangeLabelsMappingWithMajorDir(isEW) {
    return [
      { label: "Eastbound", value: isEW ? "Fwy1" : "Cross1" },
      { label: "Westbound", value: isEW ? "Fwy2" : "Cross2" },
      { label: "Northbound", value: isEW ? "Cross1" : "Fwy2" },
      { label: "Southbound", value: isEW ? "Cross2" : "Fwy1" },
    ];
  }

  /**
   * @returns {Object[]} - Array of objects with label and value properties for the lane configuration inputs
   */
  static getLabelsMappingForRoundabout() {
    return [
      {
        label: "Entry",
        value: "numEntryLanes",
        isRdbt: true,
      },
      {
        label: "Circulating",
        value: "numCircLanes",
        isRdbt: true,
      },
      {
        label: "Through",
        value: "thruLaneUtilization",
        isRdbt: true,
      },
      // Enhancement: slip lanes checkbox can be added to the UI in later version
      // {
      //   label: "Slip",
      //   value: "slipLane",
      //   isRdbt: true,
      // },
    ];
  }
}
